import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { patientsApi, type Patient, type PatientsQueryParams } from '../../services/patientsService'
import type { Api } from '../../types/generated'

// Async thunks for API calls using proper HATEOAS navigation
export const fetchPatients = createAsyncThunk(
  'patients/fetchPatients',
  async (params: PatientsQueryParams = {}, { getState }) => {
    const state = getState() as any
    const patientsHref = state.auth.currentClinic?.patients?.href
    
    if (!patientsHref) {
      throw new Error('No clinic selected or clinic missing patients link')
    }
    
    //console.log('HATEOAS: Following patients href:', patientsHref, 'with params:', params)
    
    // This is the HATEOAS way - use the href the API gave us
    const response = await patientsApi.getPatients(patientsHref, params)
    return response
  }
)

// Server-side search for patients with real-time debouncing
export const searchPatients = createAsyncThunk(
  'patients/searchPatients',
  async (searchTerm: string, { getState }) => {
    const state = getState() as any
    const patientsHref = state.auth.currentClinic?.patients?.href
    
    if (!patientsHref) {
      throw new Error('No clinic selected or clinic missing patients link')
    }
    
    console.log('HATEOAS: Searching patients with term:', searchTerm)
    
    // Use server-side search with name parameter
    const params: PatientsQueryParams = searchTerm.trim() 
      ? { name: [searchTerm] } 
      : {}
    
    const response = await patientsApi.getPatients(patientsHref, params)
    return response
  }
)

// Fetch patients for a specific page using HATEOAS URLs or page number
export const fetchPatientsPage = createAsyncThunk(
  'patients/fetchPatientsPage',
  async (params: { page?: number; hateoasUrl?: string }, { getState }) => {
    const state = getState() as any
    
    let url: string
    let queryParams: PatientsQueryParams = {}
    
    if (params.hateoasUrl) {
      // Use the HATEOAS URL, but replace {cid} placeholder with actual clinic ID
      url = params.hateoasUrl
      
      // Fix backend bug: Replace {cid} placeholder with actual clinic ID
      const clinicId = state.auth.currentClinic?.id
      if (clinicId && url.includes('{cid}')) {
        url = url.replace('{cid}', clinicId)
        console.log('HATEOAS: Replaced {cid} placeholder with clinic ID:', clinicId)
      }
      
      console.log('HATEOAS: Following pagination URL:', url)
    } else if (params.page) {
      // Use page number with base patients href
      const patientsHref = state.auth.currentClinic?.patients?.href
      
      if (!patientsHref) {
        throw new Error('No clinic selected or clinic missing patients link')
      }
      
      url = patientsHref
      queryParams = { 
        page_number: params.page,
        page_size: state.patients.pageSize 
      }
      console.log('HATEOAS: Fetching page', params.page, 'with params:', queryParams)
    } else {
      throw new Error('Either page number or HATEOAS URL must be provided')
    }
    
    const response = await patientsApi.getPatients(url, queryParams)
    return response
  }
)

export const createPatient = createAsyncThunk(
  'patients/createPatient',
  async (patientData: Api.Patient, { getState }) => {
    const state = getState() as any
    const patientsHref = state.auth.currentClinic?.patients?.href
    
    if (!patientsHref) {
      throw new Error('No clinic selected or clinic missing patients link')
    }
    
    console.log('HATEOAS: Creating patient at href:', patientsHref)
    
    // Create patient returns just id/href, so fetch full patient after creation
    const createResponse = await patientsApi.createPatient(patientsHref, patientData)
    
    // Fetch the full patient details using the returned href
    const fullPatient = await patientsApi.getPatient(createResponse.href)
    return fullPatient
  }
)

export const updatePatient = createAsyncThunk(
  'patients/updatePatient',
  async ({ patientHref, patientData }: { patientHref: string; patientData: Partial<Api.Patient> }) => {
    console.log('HATEOAS: Updating patient at href:', patientHref)
    
    // Update returns status info, so fetch full patient after update
    await patientsApi.updatePatient(patientHref, patientData)
    
    // Fetch the updated patient details using the same href
    const updatedPatient = await patientsApi.getPatient(patientHref)
    return updatedPatient
  }
)

export const deletePatient = createAsyncThunk(
  'patients/deletePatient',
  async ({ patientId, patientHref }: { patientId: string; patientHref: string }) => {
    console.log('HATEOAS: Deleting patient at href:', patientHref)
    
    await patientsApi.deletePatient(patientHref)
    return patientId  // Return ID for state management
  }
)

// State interface
interface PatientsState {
  patients: Patient[]
  total: number
  loading: boolean
  error: string | null
  searchTerm: string
  isSearching: boolean
  // HATEOAS pagination state
  pageSize: number
  pageNumber: number
  pagePrevious: string | null
  pageNext: string | null
}

// Initial state
const initialState: PatientsState = {
  patients: [],
  total: 0,
  loading: false,
  error: null,
  searchTerm: '',
  isSearching: false,
  pageSize: 10,
  pageNumber: 1,
  pagePrevious: null,
  pageNext: null,
}

// Slice
const patientsSlice = createSlice({
  name: 'patients',
  initialState,
  reducers: {
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    // Optimistic update actions
    optimisticAddPatient: (state, action) => {
      state.patients.unshift(action.payload) // Add to beginning for visibility
      state.total += 1
    },
    optimisticUpdatePatient: (state, action) => {
      const index = state.patients.findIndex(p => p.id === action.payload.id)
      if (index !== -1) {
        state.patients[index] = { ...state.patients[index], ...action.payload }
      }
    },
    optimisticDeletePatient: (state, action) => {
      state.patients = state.patients.filter(p => p.id !== action.payload)
      state.total = Math.max(0, state.total - 1)
    },
    setPageNumber: (state, action) => {
      state.pageNumber = action.payload
    },
  },
  extraReducers: (builder) => {
    // Fetch patients
    builder
      .addCase(fetchPatients.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPatients.fulfilled, (state, action) => {
        state.loading = false
        state.patients = action.payload.patients
        state.total = action.payload.total
        state.pageSize = action.payload.page_size || state.pageSize
        state.pageNumber = action.payload.page_number || state.pageNumber
        state.pagePrevious = action.payload.page_previous
        state.pageNext = action.payload.page_next
      })
      .addCase(fetchPatients.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch patients'
      })

    // Create patient
    builder
      .addCase(createPatient.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createPatient.fulfilled, (state, action) => {
        state.loading = false
        state.patients.push(action.payload)
        state.total += 1
      })
      .addCase(createPatient.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to create patient'
      })

    // Update patient
    builder
      .addCase(updatePatient.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updatePatient.fulfilled, (state, action) => {
        state.loading = false
        const index = state.patients.findIndex(p => p.id === action.payload.id)
        if (index !== -1) {
          state.patients[index] = action.payload
        }
      })
      .addCase(updatePatient.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to update patient'
      })

    // Delete patient
    builder
      .addCase(deletePatient.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deletePatient.fulfilled, (state, action) => {
        state.loading = false
        state.patients = state.patients.filter(p => p.id !== action.payload)
        state.total -= 1
      })
      .addCase(deletePatient.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to delete patient'
      })

    // Search patients
    builder
      .addCase(searchPatients.pending, (state) => {
        state.isSearching = true
        state.error = null
      })
      .addCase(searchPatients.fulfilled, (state, action) => {
        state.isSearching = false
        state.patients = action.payload.patients
        state.total = action.payload.total
        state.pageSize = action.payload.page_size || state.pageSize
        state.pageNumber = action.payload.page_number || 1 // Reset to page 1 on search
        state.pagePrevious = action.payload.page_previous
        state.pageNext = action.payload.page_next
      })
      .addCase(searchPatients.rejected, (state, action) => {
        state.isSearching = false
        state.error = action.error.message || 'Failed to search patients'
      })

    // Fetch patients page
    builder
      .addCase(fetchPatientsPage.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPatientsPage.fulfilled, (state, action) => {
        state.loading = false
        state.patients = action.payload.patients
        state.total = action.payload.total
        state.pageSize = action.payload.page_size || state.pageSize
        state.pageNumber = action.payload.page_number || state.pageNumber
        state.pagePrevious = action.payload.page_previous
        state.pageNext = action.payload.page_next
      })
      .addCase(fetchPatientsPage.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch patients page'
      })
  },
})

// Export actions
export const { 
  setSearchTerm, 
  clearError, 
  setPageSize, 
  setPageNumber,
  optimisticAddPatient,
  optimisticUpdatePatient,
  optimisticDeletePatient
} = patientsSlice.actions

// Selectors
export const selectPatients = (state: { patients: PatientsState }) => state.patients.patients
export const selectPatientsTotal = (state: { patients: PatientsState }) => state.patients.total
export const selectPatientsLoading = (state: { patients: PatientsState }) => state.patients.loading
export const selectPatientsError = (state: { patients: PatientsState }) => state.patients.error
export const selectSearchTerm = (state: { patients: PatientsState }) => state.patients.searchTerm
export const selectIsSearching = (state: { patients: PatientsState }) => state.patients.isSearching
export const selectPageSize = (state: { patients: PatientsState }) => state.patients.pageSize
export const selectPageNumber = (state: { patients: PatientsState }) => state.patients.pageNumber
export const selectPagePrevious = (state: { patients: PatientsState }) => state.patients.pagePrevious
export const selectPageNext = (state: { patients: PatientsState }) => state.patients.pageNext

// Pagination data selector for use with PaginationControls
export const selectPatientsPagination = (state: { patients: PatientsState }) => ({
  currentPage: state.patients.pageNumber,
  pageSize: state.patients.pageSize,
  totalItems: state.patients.total,
  totalPages: Math.ceil(state.patients.total / state.patients.pageSize),
  hasNextPage: !!state.patients.pageNext,
  hasPreviousPage: !!state.patients.pagePrevious,
  pageNext: state.patients.pageNext,
  pagePrevious: state.patients.pagePrevious,
})

// Since we now use server-side search, this selector just returns the patients
// The filtering is done on the server when searchPatients is called
export const selectFilteredPatients = (state: { patients: PatientsState }): Patient[] => {
  return state.patients.patients
}

export default patientsSlice.reducer